import styled from 'styled-components';
import { Grid20 } from '@elfsight/icons';
import { Icon as LibIcon } from '@elfsight/lib-icon';
import type { WithSX } from '@elfsight/lib-utils';
import { applySX } from '@elfsight/lib-utils';
import {
  createReadableClass,
  getHandleActionKeyDown,
  getLineHeight
} from '@elfsight/widget-utils';
import type { SourceId } from '../../constants';
import { getSourceMetaValue } from '../../constants';
import { useSocialFeedSettings } from '../utils';
import { useTabs } from './TabsProvider';

interface TabItemProps {
  id: string;
  type: 'all' | SourceId;
  name: string;
  onClick: () => void;
}

export function TabItem({ id, type, name, onClick }: TabItemProps) {
  const {
    tabsTextColor,
    tabElements,
    tabsTextStyle: { fontSize },
    tabsSourceImageColor: { type: iconColorType, color: iconColor }
  } = useSocialFeedSettings();
  const {
    currentTab: { id: currentTabId }
  } = useTabs();
  const { icon, nativeIcon } = getSourceMetaValue(type);

  const isAllTab = type === 'all';
  const isActive = id === currentTabId;
  const allTabColor =
    isAllTab && iconColorType === 'native' ? tabsTextColor : iconColor;

  const displayIcon = tabElements.includes('icon');
  const displaySourceName = isAllTab || tabElements.includes('name');

  const iconSrc = (() => {
    if (isAllTab) {
      return undefined;
    }

    if (iconColorType === 'native') {
      return nativeIcon;
    }

    return icon;
  })();

  return (
    <Container
      tabIndex={0}
      onClick={onClick}
      onKeyDown={getHandleActionKeyDown(onClick)}
      _textColor={tabsTextColor}
      _isActive={isActive}
    >
      {displayIcon && (
        <Icon
          src={iconSrc}
          component={isAllTab ? Grid20 : undefined}
          fill={allTabColor}
          size={20}
        />
      )}

      {displaySourceName && (
        <Name
          $sx={{
            fontSize,
            lineHeight: `${getLineHeight({ fontSize })}px`,
            color: tabsTextColor
          }}
        >
          {name}
        </Name>
      )}
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'tab-item',
    element: 'container'
  })
})<{
  _isActive: boolean;
  _textColor: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
  cursor: pointer;
  font-weight: ${({ _isActive }) => (_isActive ? 600 : 400)};

  &:focus {
    outline: none;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    display: block;
    top: 6px;
    right: -12px;
    bottom: 6px;
    left: -12px;
    border-radius: 30px;
    opacity: 0;
    transform: scale(0.8);
    background-color: ${({ _textColor }) => _textColor};
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  &:hover::before,
  &:focus-visible::before {
    opacity: 0.05;
    transform: scale(1);
  }

  &::after {
    content: '';
    position: absolute;
    display: block;
    right: 0;
    bottom: 0;
    left: 0;
    height: 1px;
    transform: ${({ _isActive }) => (_isActive ? 'scaleY(1)' : 'scaleY(0)')};
    transform-origin: 50% 100%;
    background-color: ${({ _textColor }) => _textColor};
  }
`;

const Icon = styled(LibIcon).attrs({
  className: createReadableClass({
    component: 'tab-item',
    element: 'icon'
  })
})``;

const Name = styled.div.attrs({
  className: createReadableClass({
    component: 'tab-item',
    element: 'source-name'
  })
})<WithSX>`
  ${applySX};
  text-wrap: nowrap;
`;
